import React, { useState } from "react";
import api from "@/lib/api";
import { MagnifyingGlass, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";

export default function DomainChecker() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const check = async (e) => {
    e?.preventDefault?.();
    setError("");
    setResult(null);
    const d = domain.trim().toLowerCase();
    if (!/^[a-z0-9-]+\.[a-z0-9.-]+$/.test(d)) {
      setError("Enter a valid domain like maxine.consulting");
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/domain/check`, { params: { domain: d } });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="domain-checker" className="border border-[color:var(--ink)] bg-white p-6">
      <div className="flex items-center justify-between">
        <div className="eyebrow">Domain checker</div>
        <span className="font-mono text-[10px] text-neutral-500 uppercase">RDAP · live</span>
      </div>
      <p className="mt-3 font-display font-bold text-2xl leading-tight">Is your name still available?</p>
      <form onSubmit={check} className="mt-5">
        <div className="flex border border-neutral-300 focus-within:border-[color:var(--brand)]">
          <input
            data-testid="domain-input"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="yourbrand.com"
            aria-label="Domain to check"
            className="flex-1 px-4 py-3 outline-none font-mono text-sm"
          />
          <button
            data-testid="domain-check-btn"
            type="submit"
            disabled={loading}
            className="bg-[color:var(--brand)] text-white px-4 flex items-center gap-2 hover:bg-[color:var(--brand-dark)] transition-colors duration-200 disabled:opacity-60"
          >
            <MagnifyingGlass size={16} weight="bold" />
            <span className="text-sm font-semibold">{loading ? "..." : "Check"}</span>
          </button>
        </div>
      </form>
      {error && (
        <div data-testid="domain-error" className="mt-4 flex items-center gap-2 text-sm text-[color:var(--brand)]">
          <Warning size={16} weight="bold" /> {error}
        </div>
      )}
      {result && result.available === true && (
        <div data-testid="domain-available" className="mt-5 border border-emerald-600/40 bg-emerald-50 p-4">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold">
            <CheckCircle size={18} weight="fill" /> Available
          </div>
          <p className="text-sm text-neutral-700 mt-1"><span className="font-mono">{result.domain}</span> is free to register.</p>
          <a
            href={`https://wa.me/7054986771?text=${encodeURIComponent(`I want to register ${result.domain}. Please help.`)}`}
            target="_blank" rel="noreferrer" data-testid="domain-book"
            className="btn-brand mt-4 text-sm py-2.5 px-4"
          >
            Book it via WhatsApp
          </a>
        </div>
      )}
      {result && result.available === false && (
        <div data-testid="domain-taken" className="mt-5 border border-neutral-300 bg-neutral-50 p-4">
          <div className="flex items-center gap-2 text-neutral-800 font-semibold">
            <XCircle size={18} weight="fill" /> Taken
          </div>
          <p className="text-sm text-neutral-700 mt-1"><span className="font-mono">{result.domain}</span> is already registered.</p>
          {result.registrar && <p className="text-xs text-neutral-500 mt-1">Registrar: {result.registrar}</p>}
          {result.expires && <p className="text-xs text-neutral-500">Expires: {String(result.expires).slice(0, 10)}</p>}
        </div>
      )}
      {result && result.available === null && (
        <div data-testid="domain-unknown" className="mt-5 border border-amber-400 bg-amber-50 p-4 text-sm text-amber-800">
          Registry didn&apos;t respond. Try again in a moment.
        </div>
      )}
      <p className="mt-4 font-mono text-[10px] text-neutral-500 uppercase tracking-widest">Supports .com, .ae, .in, .eu and 900+ TLDs</p>
    </div>
  );
}
